import { useState, useEffect } from 'react';
import { X, Save, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = ['material', 'labor', 'transport', 'equipment', 'vendor', 'food', 'site', 'printing', 'travel', 'other'];
const PAY_STATUS = ['paid', 'pending', 'partial'];

const genExpId = () => `QEXP-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000) + 1000}`;

export default function QuotationExpenseForm({ quotation, editingExpense, onClose, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!editingExpense?.id;

  const [form, setForm] = useState({
    expenseId: genExpId(),
    category: 'material',
    description: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    paidFrom: '',
    paidTo: '',
    paymentStatus: 'paid',
    notes: '',
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit && editingExpense) {
      setForm({
        expenseId: editingExpense.expenseId || genExpId(),
        category: editingExpense.category || 'material',
        description: editingExpense.description || '',
        amount: editingExpense.amount ?? '',
        date: editingExpense.date ? editingExpense.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
        paidFrom: editingExpense.paidFrom || '',
        paidTo: editingExpense.paidTo || '',
        paymentStatus: editingExpense.paymentStatus || 'paid',
        notes: editingExpense.notes || '',
      });
    }
  }, [editingExpense, isEdit]);

  const onChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) {
      toast.error('Description and Amount required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        expenseId: form.expenseId,
        quotationType: quotation.type,
        quotation: quotation.type === 'architecture' ? quotation.quotationId : null,
        interiorQuotation: quotation.type === 'interior' ? quotation.quotationId : null,
        clientName: quotation.clientName,
        quoteNumber: quotation.quoteNumber,
        category: form.category,
        description: form.description,
        amount: Number(form.amount) || 0,
        date: form.date || null,
        paidFrom: form.paidFrom || '',
        paidTo: form.paidTo || '',
        paymentStatus: form.paymentStatus,
        notes: form.notes || '',
        createdBy: user?.id || null,
      };

      if (isEdit) {
        await pb.collection('oc_project_expenses').update(editingExpense.id, payload);
        toast.success('Expense updated');
      } else {
        await pb.collection('oc_project_expenses').create(payload);
        toast.success('Expense added');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this expense?')) return;
    try {
      await pb.collection('oc_project_expenses').delete(editingExpense.id);
      toast.success('Deleted');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  const inp = 'w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none text-sm';
  const lbl = 'block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex justify-between items-center text-white">
          <div>
            <h2 className="text-lg font-bold">{isEdit ? 'Edit Expense' : 'Add Expense'}</h2>
            <p className="text-xs text-white/70 mt-0.5">
              {quotation.type === 'architecture' ? '🏗️' : '🎨'} {quotation.clientName} · {quotation.quoteNumber}
            </p>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-lg p-1.5 transition"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Quotation info readonly */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-center justify-between text-xs">
            <span className="text-slate-600">Quote Total</span>
            <span className="font-bold text-slate-800">₹{(quotation.total || 0).toLocaleString('en-IN')}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Category *</label>
              <select name="category" value={form.category} onChange={onChange} className={inp} required>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Date</label>
              <input type="date" name="date" value={form.date} onChange={onChange} className={inp} />
            </div>
          </div>

          <div>
            <label className={lbl}>Description *</label>
            <input name="description" value={form.description} onChange={onChange} required
              className={inp} placeholder="e.g., Site visit, Drawing printout, Travel" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Amount (₹) *</label>
              <input type="number" name="amount" value={form.amount} onChange={onChange} required min="0" step="0.01"
                className={inp} placeholder="0" />
            </div>
            <div>
              <label className={lbl}>Payment Status</label>
              <select name="paymentStatus" value={form.paymentStatus} onChange={onChange} className={inp}>
                {PAY_STATUS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Paid From</label>
              <input name="paidFrom" value={form.paidFrom} onChange={onChange}
                className={inp} placeholder="Cash / office_fund" />
            </div>
            <div>
              <label className={lbl}>Paid To</label>
              <input name="paidTo" value={form.paidTo} onChange={onChange}
                className={inp} placeholder="Vendor / person name" />
            </div>
          </div>

          <div>
            <label className={lbl}>Notes</label>
            <textarea name="notes" value={form.notes} onChange={onChange} rows={2} className={inp}
              placeholder="Any additional details..." />
          </div>
        </form>

        <div className="border-t border-slate-100 px-5 py-3 flex justify-between items-center gap-3 bg-slate-50">
          {isEdit ? (
            <button type="button" onClick={handleDelete}
              className="flex items-center gap-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg transition text-sm font-medium">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="hover:bg-slate-100 transition">Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-105 active:scale-95 transition-all shadow-lg">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> {isEdit ? 'Update' : 'Add Expense'}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
